import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API from '../../config/api';
import './QuratorHeader.css';

const QuratorHeader = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profileDropdownRef = useRef(null);
  const qrCodeRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate QR Code
  useEffect(() => {
    const generateQRCode = async () => {
      if (qrModalOpen && user && qrCodeRef.current) {
        try {
          // Fetch encrypted QR code string from backend
          const response = await API.get('/qurator/profile/qr-code');
          if (response.data.success && response.data.encrypted_string) {
            // Clear previous content
            qrCodeRef.current.innerHTML = '';
            
            // Create QR code using external API with encrypted string
            const img = document.createElement('img');
            img.src = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(response.data.encrypted_string)}`;
            img.alt = 'QR Code';
            img.style.width = '100%';
            img.style.maxWidth = '256px';
            img.style.height = 'auto';
            qrCodeRef.current.appendChild(img);
          }
        } catch (error) {
          console.error('Error generating QR code:', error);
          qrCodeRef.current.innerHTML = '<p style="color: red;">Error loading QR code</p>';
        }
      }
    };
    
    generateQRCode();
  }, [qrModalOpen, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className="qrt-header">
        <div className="qrt-header-left">
          <button 
            className="qrt-sidebar-toggle" 
            type="button" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle Sidebar"
          >
            <i className="bi bi-list"></i>
          </button>
          <Link to="/qurator-dashboard" className="header-brand me-auto">
            <img src="https://qure.science/images/qure.webp" alt="Qure" className="header-logo" />
          </Link>
          <span className="qrt-user-type-badge">Qurator</span>
        </div>
        
        <div className="qrt-header-right">
          <button 
            className="qrt-qr-button" 
            onClick={() => setQrModalOpen(true)}
            type="button"
          >
            <i className="bi bi-qr-code"></i>
            <span>QR Code</span>
          </button>

          <div className="qrt-profile-dropdown" ref={profileDropdownRef}>
            <button 
              className="qrt-profile-menu" 
              type="button"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <img 
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=FF5722&color=fff`}
                alt="Profile" 
                className="qrt-profile-img"
              />
              <span className="qrt-profile-name">{user?.username || 'User'}</span>
              <i className={`bi bi-chevron-down qrt-profile-chevron ${profileDropdownOpen ? 'rotated' : ''}`}></i>
            </button>
            {profileDropdownOpen && (
              <div className="qrt-dropdown-menu show">
                <Link className="qrt-dropdown-item" to="/profile" onClick={() => setProfileDropdownOpen(false)}>
                  <i className="bi bi-person"></i>Profile
                </Link>
                {(user?.user_id === 79 || user?.user_id === '79' || parseInt(user?.user_id) === 79) && (
                  <Link className="qrt-dropdown-item" to="/admin-dashboard" onClick={() => setProfileDropdownOpen(false)}>
                    <i className="bi bi-shield-check"></i>Admin Dashboard
                  </Link>
                )}
                <hr className="qrt-dropdown-divider" />
                <button className="qrt-dropdown-item" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right"></i>Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* QR Code Modal */}
      {qrModalOpen && (
        <div className="qrt-qr-modal show" onClick={() => setQrModalOpen(false)}>
          <div className="qrt-qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="qrt-qr-modal-header">
              <h3 className="qrt-qr-modal-title">
                <i className="bi bi-qr-code"></i> Your QR Code
              </h3>
              <button 
                className="qrt-qr-close" 
                onClick={() => setQrModalOpen(false)}
                type="button"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="qrt-qr-modal-body">
              <div className="qrt-qr-code-container">
                <div id="qrtQRCode" ref={qrCodeRef}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`qrt-sidebar ${sidebarOpen ? 'qrt-show' : ''}`} id="qrtSidebar">
        <nav>
          <Link 
            to="/qurator-dashboard" 
            className={`qrt-nav-link ${isActive('/qurator-dashboard') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-house"></i>Dashboard
          </Link>
          <Link 
            to="/library" 
            className={`qrt-nav-link ${isActive('/library') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-collection"></i>Library
          </Link>
          <Link 
            to="/projects" 
            className={`qrt-nav-link ${isActive('/projects') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-folder"></i>Projects
          </Link>
          <Link 
            to="/team-management" 
            className={`qrt-nav-link ${isActive('/team-management') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-people"></i>Team Management
          </Link>
          <Link 
            to="/global-context" 
            className={`qrt-nav-link ${isActive('/global-context') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-globe"></i>Global Context
          </Link>

          <div className="qrt-nav-section">Workflow</div>
          <Link 
            to="/calendar" 
            className={`qrt-nav-link ${isActive('/calendar') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-calendar-event"></i>Calendar
          </Link>
          <Link 
            to="/analytics" 
            className={`qrt-nav-link ${isActive('/analytics') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-graph-up"></i>Analytics
          </Link>

          <div className="qrt-nav-section">Support</div>
          <Link 
            to="/help-desk" 
            className={`qrt-nav-link ${isActive('/help-desk') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset"></i>Help Desk
          </Link>
          <Link 
            to="/support" 
            className={`qrt-nav-link ${isActive('/support') ? 'qrt-active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset"></i>Support Ticket
          </Link>
        </nav>
      </aside>

    </>
  );
};

export default QuratorHeader;
